import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { BILL_KINDS, formatIqd, fromMillidinars, type BillKind, type BillRowDto } from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { FilterDisclosure } from '@/components/layout/FilterDisclosure';
import { BillsTable, type BillAction } from './BillsTable';
import { BillDetailsDrawer } from './BillDetailsDrawer';
import { EditBillDialog } from './EditBillDialog';
import { DeleteBillDialog } from './DeleteBillDialog';
import {
  defaultRange,
  receiptNumberLike,
  useBillFilterOptions,
  useBills,
  type BillFilters,
} from './useBills';

const PAGE_SIZE = 50;

/**
 * Bills (SPEC §5.6).
 *
 * Four sub-tabs over one grid. The whole screen is behind `bills.view`; §3 is
 * explicit that the tab is not shown at all without it, and the route in
 * App.tsx enforces the same thing so the page is never reachable by URL.
 */
export function BillsPage() {
  const { t, i18n } = useTranslation();
  const { can, venue } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';
  // SPEC §3: without `bills.view` the tab is not shown AT ALL. The sidebar
  // already hides the entry; this stops the screen being reachable by URL.
  // The API refuses the same way, which is the gate that actually matters.
  const allowed = can('bills.view');

  const { options, error: optionsError } = useBillFilterOptions();

  const [kind, setKind] = useState<BillKind>('RESERVATION');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  /*
   * The receipt-number lookup (owner, 2026-09-23: "so if a customer comes back
   * to us about it we can search for it on our end"). Its own box rather than
   * a mode of the search above: "12" there already means device 12 or invoice
   * C12, and a receipt is answered across every date and tab, which the
   * search deliberately is not.
   */
  const [receiptDraft, setReceiptDraft] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [deviceCategoryId, setDeviceCategoryId] = useState<string | null>(null);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [editing, setEditing] = useState<BillRowDto | null>(null);
  const [deleting, setDeleting] = useState<BillRowDto | null>(null);

  // Seed the pickers from the venue's own business day once settings arrive:
  // Opening Time on today's business date through Closing Time next morning.
  useEffect(() => {
    if (!options || from || to) return;
    const range = defaultRange(options);
    setFrom(range.from);
    setTo(range.to);
  }, [options, from, to]);

  // Typing an invoice number should not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchDraft), 300);
    return () => clearTimeout(timer);
  }, [searchDraft]);

  useEffect(() => {
    const timer = setTimeout(() => setReceiptNumber(receiptDraft.trim()), 300);
    return () => clearTimeout(timer);
  }, [receiptDraft]);

  // Any filter change invalidates the page cursor: staying on page 4 of a
  // result set that now has one page shows an empty grid for no visible reason.
  useEffect(() => {
    setPage(1);
  }, [kind, search, deviceCategoryId, employeeId, from, to, receiptNumber]);

  const filters = useMemo<BillFilters | null>(
    () =>
      from && to ? { kind, search, deviceCategoryId, employeeId, from, to, receiptNumber } : null,
    [kind, search, deviceCategoryId, employeeId, from, to, receiptNumber],
  );
  const lookingUpReceipt = receiptNumber !== '';
  // A bare number in the ordinary search is as often as not the one off a
  // receipt; the server matches it inside the window on screen, and this
  // offers the lookup that is not bound by it.
  const searchedReceipt = lookingUpReceipt ? null : receiptNumberLike(search);
  const lookUpReceipt = (number: string) => {
    setReceiptDraft(number);
    setReceiptNumber(number);
  };

  const { data, loading, error, reload } = useBills(filters, page, PAGE_SIZE);

  const controlClass =
    'rounded-control border border-subtle bg-input px-2.5 py-1.5 text-sm text-content outline-none focus:border-primary';

  const summaryTotal = data
    ? formatIqd(fromMillidinars(data.summary.totalMd), { currencySymbol, locale: i18n.language })
    : '—';

  if (!allowed) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-content">{t('bills.title')}</h1>
        <p className="mt-2 text-sm text-content-muted">{t('bills.noViewPermission')}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <header className="px-4 pt-4">
        <h1 className="text-xl font-semibold text-content">{t('bills.title')}</h1>

        {/* Sub-tabs: Reservations | Cashier | Portal Orders | Refill */}
        <nav className="mt-3 flex gap-1 overflow-x-auto border-b border-subtle" aria-label={t('bills.title')}>
          {BILL_KINDS.map((candidate) => (
            <button
              key={candidate}
              type="button"
              onClick={() => setKind(candidate)}
              aria-current={candidate === kind ? 'page' : undefined}
              className={`-mb-px border-b-2 px-4 py-2 text-sm transition-colors ${
                candidate === kind
                  ? // The live tab carries the current in text AND underline
                    // (card `.tabs a.on`) — the strip reads as a circuit with
                    // one energised trace.
                    'border-primary font-medium text-primary'
                  : 'border-transparent text-content-secondary hover:text-content'
              }`}
            >
              {t(`bills.tabs.${tabKey(candidate)}`)}
            </button>
          ))}
        </nav>
      </header>

      <div className="flex flex-wrap items-end gap-3 p-4">
        <label className="relative block">
          <span className="sr-only">{t('common.search')}</span>
          <Search
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted"
            style={{ insetInlineStart: '0.6rem' }}
            aria-hidden
          />
          <input
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder={t('bills.searchPlaceholder')}
            className={`${controlClass} w-56 ps-8 sm:w-80`}
          />
        </label>

        <label className="block">
          <span className="sr-only">{t('serviceCharge.bills.receiptNumber')}</span>
          <input
            value={receiptDraft}
            onChange={(event) => setReceiptDraft(event.target.value)}
            placeholder={t('serviceCharge.bills.searchReceiptPlaceholder')}
            inputMode="numeric"
            data-receipt-lookup
            className={`${controlClass} w-44 tabular`}
          />
        </label>

        <FilterDisclosure badge={(deviceCategoryId ? 1 : 0) + (employeeId ? 1 : 0) + (from ? 1 : 0) + (to ? 1 : 0)}>

        <label className="block">
          <span className="mb-1 block text-xs text-content-muted">{t('bills.deviceSection')}</span>
          <select
            value={deviceCategoryId ?? ''}
            onChange={(event) => setDeviceCategoryId(event.target.value || null)}
            className={controlClass}
          >
            <option value="">{t('bills.allSections')}</option>
            {(options?.deviceSections ?? []).map((section) => (
              <option key={section.id} value={section.id}>
                {section.name}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs text-content-muted">{t('bills.user')}</span>
          <select
            value={employeeId ?? ''}
            onChange={(event) => setEmployeeId(event.target.value || null)}
            className={controlClass}
          >
            <option value="">{t('bills.allUsers')}</option>
            {(options?.employees ?? []).map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.name}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs text-content-muted">{t('common.from')}</span>
          <input
            type="datetime-local"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            className={`${controlClass} tabular`}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs text-content-muted">{t('common.to')}</span>
          <input
            type="datetime-local"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            className={`${controlClass} tabular`}
          />
        </label>
        </FilterDisclosure>
      </div>

      {lookingUpReceipt ? (
        <div className="flex flex-wrap items-center gap-3 px-4 pb-2">
          <p className="text-sm font-medium text-content">
            {t('serviceCharge.bills.receiptResults', {
              number: receiptNumber.replace(/^#\s*/, ''),
            })}
          </p>
          <p className="text-xs text-content-muted">
            {t('serviceCharge.bills.receiptResultsNote')}
          </p>
          <button
            type="button"
            onClick={() => {
              setReceiptDraft('');
              setReceiptNumber('');
            }}
            className="rounded-full border border-subtle px-3 py-1 text-xs text-content-secondary hover:bg-hover"
          >
            {t('serviceCharge.bills.clearReceipt')}
          </button>
        </div>
      ) : data ? (
        <div className="flex flex-wrap items-center gap-3 px-4 pb-2">
          <p className="text-xs text-content-muted">
            {t('bills.businessDayNote', {
              from: data.appliedRange.fromBusinessDate,
              to: data.appliedRange.toBusinessDate,
            })}
          </p>
          {searchedReceipt ? (
            <button
              type="button"
              data-look-up-receipt
              onClick={() => lookUpReceipt(searchedReceipt)}
              className="rounded-full border border-subtle px-3 py-1 text-xs text-content-secondary hover:bg-hover"
            >
              {t('serviceCharge.bills.lookUpReceipt', { number: searchedReceipt })}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto px-4">
        {(optionsError ?? error) ? (
          <p role="alert" className="text-sm text-danger">
            {optionsError ?? error}
          </p>
        ) : loading && !data ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : !data || data.rows.length === 0 ? (
          <p className="text-sm text-content-muted">{t('common.noResults')}</p>
        ) : lookingUpReceipt ? (
          /*
           * One receipt can carry a reservation AND a cafe order, and each kind
           * has its own columns — so the lookup answers with one table per kind
           * present rather than forcing every row into the tab's columns.
           */
          <div className="space-y-6 pb-4">
            {BILL_KINDS.filter((candidate) => data.rows.some((row) => row.kind === candidate)).map(
              (candidate) => (
                <section key={candidate}>
                  <h2 className="mb-2 text-sm font-semibold text-content-secondary">
                    {t(`bills.tabs.${tabKey(candidate)}`)}
                  </h2>
                  <BillsTable
                    kind={candidate}
                    rows={data.rows.filter((row) => row.kind === candidate)}
                    currencySymbol={currencySymbol}
                    canEdit={can('bills.edit')}
                    canDelete={can('bills.delete')}
                    onAction={handleAction}
                  />
                </section>
              ),
            )}
          </div>
        ) : (
          <BillsTable
            kind={kind}
            rows={data.rows}
            currencySymbol={currencySymbol}
            canEdit={can('bills.edit')}
            canDelete={can('bills.delete')}
            onAction={handleAction}
          />
        )}
      </div>

      {/* Footer summary bar (SPEC §5.6) — Item Count and Total describe the
          whole filtered set, not the rows currently on screen. */}
      <footer className="flex items-center gap-6 border-t border-subtle bg-raised px-4 py-3">
        <span className="text-sm text-content-secondary">
          {t('common.itemCount')}:{' '}
          <span className="tabular font-medium text-content">{data?.summary.itemCount ?? 0}</span>
        </span>
        <span className="text-sm text-content-secondary">
          {t('common.total')}:{' '}
          {/* The range's takings — the figure this screen answers for. */}
          <span className="tabular text-lg font-bold text-primary [text-shadow:var(--glow-primary)]">
            {summaryTotal}
          </span>
        </span>

        <div className="flex-1" />

        {data && (data.hasMore || page > 1) ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page === 1}
              aria-label={t('bills.previousPage')}
              className="grid h-7 w-7 place-items-center rounded-control text-content-secondary hover:bg-hover disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
            </button>
            <span className="tabular text-sm text-content-secondary">{page}</span>
            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={!data.hasMore}
              aria-label={t('bills.nextPage')}
              className="grid h-7 w-7 place-items-center rounded-control text-content-secondary hover:bg-hover disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
            </button>
          </div>
        ) : null}
      </footer>

      {detailsId ? (
        <BillDetailsDrawer
          billId={detailsId}
          currencySymbol={currencySymbol}
          onClose={() => setDetailsId(null)}
        />
      ) : null}

      {editing ? (
        <EditBillDialog
          bill={editing}
          currencySymbol={currencySymbol}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteBillDialog
          bill={deleting}
          currencySymbol={currencySymbol}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void reload();
          }}
        />
      ) : null}
    </div>
  );

  function handleAction(bill: BillRowDto, action: BillAction): void {
    switch (action) {
      case 'details':
        setDetailsId(bill.id);
        break;
      case 'print':
        // The drawer owns the printable layout, so printing is "open it and
        // print", which also means what is printed is what was reviewed.
        setDetailsId(bill.id);
        break;
      case 'edit':
        setEditing(bill);
        break;
      case 'delete':
        setDeleting(bill);
        break;
    }
  }
}

function tabKey(kind: BillKind): string {
  return {
    RESERVATION: 'reservations',
    CASHIER: 'cashier',
    PORTAL_ORDER: 'portalOrders',
    REFILL: 'refill',
  }[kind];
}
